import { lineTotal, sumMoney } from '@mms/shared';
import { useEffect, type ReactNode } from 'react';
import { Alert } from 'react-bootstrap';
import { z } from 'zod';
import type { Row } from '../api/types';
import { amountReq, dateTimeReq, fxRateReq, selectOpt, selectReq, toId, toIdOrNull, toText } from '../forms/zod';
import { BalanceCard, useBalance } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { Amount, fromDateTimeInput, nowInput, toDateTimeInput } from '../ui/format';
import { toOptions, useLookup } from '../ui/lookups';
import { Segmented } from '../ui/Segmented';
import { FieldRenderer, type CrudConfig, type FieldDef } from './CrudScreen';
import { TROUGH_BANK, TROUGH_CASH, base, dateCol, noCol, useBankPicker } from './transactions';

/**
 * Payment (a payment to a Seller), redesigned 2026-10-13 (migration 023). You PAY `payAmount` in the Pay currency, FROM Cash, a Bank or a
 * Dispatcher's account, and that settles the Seller's dues in the Seller's own currency: dues settled = payAmount / rate (three decimals), where
 * 1 Seller-currency unit = rate Pay-currency units. The Seller's balance goes UP (toward 0) by the dues settled - a purchase made it negative; the source goes down by what you paid.
 * Form values: currencyId = the Seller's currency, payCurrencyId = the Pay currency, troughId '1' Cash / '2' Bank / DISPATCHER (form-only, sent as a null
 * trough with a supplierId).
 */
const DISPATCHER = '3';
const toTrough = (v: string) => (v === DISPATCHER ? null : toId(v));

const paymentSchema = z
  .object({
    currencyId: selectReq('Select the seller currency'), purchaserId: selectReq('Select a seller'),
    payCurrencyId: selectReq('Select the pay currency'),
    troughId: z.string(), bankId: selectOpt, supplierId: selectOpt,
    payAmount: amountReq, exchangeRate: fxRateReq, transactionDate: dateTimeReq, details: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.troughId === TROUGH_BANK && !v.bankId) ctx.addIssue({ code: 'custom', path: ['bankId'], message: 'Select a bank' });
    if (v.troughId === DISPATCHER && !v.supplierId) ctx.addIssue({ code: 'custom', path: ['supplierId'], message: 'Select a dispatcher' });
  });

/** A seller / dispatcher lookup row holds a currency? */
const holds = (p: Row, currencyId: string) => ((p.currencies as Row[] | undefined) ?? []).some((c) => String(c.currencyId) === currencyId);

export const settlementsConfig: CrudConfig = {
  ...base,
  title: 'Payment', singular: 'payment', icon: 'wallet2', menu: 'payment', endpoint: '/transactions/settlement',
  modalSize: 'xl',
  schema: paymentSchema,
  defaults: () => ({ currencyId: '', purchaserId: '', payCurrencyId: '', troughId: TROUGH_CASH, bankId: '', supplierId: '', payAmount: '', exchangeRate: '', transactionDate: nowInput(), details: '' }),
  toForm: (r) => ({
    currencyId: String(r.currencyId), purchaserId: String(r.purchaserId), payCurrencyId: String(r.payCurrencyId),
    troughId: r.troughId == null ? DISPATCHER : String(r.troughId), bankId: r.bankId ? String(r.bankId) : '', supplierId: r.supplierId ? String(r.supplierId) : '',
    payAmount: r.payAmount, exchangeRate: String(Number(r.exchangeRate)), transactionDate: toDateTimeInput(r.transactionDate), details: r.details ?? '',
  }),
  toPayload: (v) => ({
    purchaserId: toId(v.purchaserId), currencyId: toId(v.currencyId), payCurrencyId: toId(v.payCurrencyId), payAmount: v.payAmount.trim(), exchangeRate: v.exchangeRate.trim(),
    troughId: toTrough(v.troughId), bankId: v.troughId === TROUGH_BANK ? toIdOrNull(v.bankId) : null, supplierId: v.troughId === DISPATCHER ? toIdOrNull(v.supplierId) : null,
    transactionDate: fromDateTimeInput(v.transactionDate), details: toText(v.details),
  }),
  rowLabel: (r) => `${r.transactionNo} (${r.partyName})`,
  useTable: () => {
    const purchasers = useLookup('purchasers');
    const suppliers = useLookup('suppliers');
    // #Ref, Date, Seller, Dues settled (Seller currency), You paid (Pay currency), Rate, Paid from, Details
    const columns: Column[] = [
      { ...noCol, header: '#Ref', hideOnMobile: false },
      dateCol,
      { key: 'partyName', header: 'Seller', sortKey: 'party', className: 'fw-semibold' },
      { key: 'amount', header: 'Dues settled', sortKey: 'amount', align: 'end', render: (r) => <Amount value={r.amount} decimals={3} code={r.currencyCode} /> },
      { key: 'payAmount', header: 'You paid', align: 'end', render: (r) => <Amount value={r.payAmount} code={r.payCurrencyCode} /> },
      {
        key: 'exchangeRate', header: 'Rate', align: 'end', hideOnMobile: true,
        render: (r) => <>{Number(r.exchangeRate).toString()}{r.payCurrencyCode ? <small className="text-body-secondary ms-1">{r.payCurrencyCode}</small> : null}</>,
      },
      {
        key: 'trough', header: 'Paid from', hideOnMobile: true,
        render: (r) => (r.troughId == null
          ? <>Dispatcher<small className="text-body-secondary ms-1">{r.supplierName}</small></>
          : r.bankName ? <>{r.trough}<small className="text-body-secondary ms-1">{r.bankName}</small></> : <>{r.trough}</>),
      },
      { key: 'details', header: 'Details', className: 'text-truncate', hideOnMobile: true, render: (r) => <span title={r.details ?? ''} className="d-inline-block text-truncate" style={{ maxWidth: 220 }}>{r.details ?? ''}</span> },
    ];
    return {
      columns,
      filters: [
        { param: 'purchaserId', label: 'Seller', options: toOptions(purchasers.data) },
        { param: 'supplierId', label: 'Dispatcher', options: toOptions(suppliers.data) },
        { param: 'troughId', label: 'Via', options: [{ value: '1', label: 'Cash' }, { value: '2', label: 'Bank' }] },
      ],
    };
  },
  useForm: (form, { readOnly, mode }) => {
    const currencies = useLookup('currencies');
    const purchasers = useLookup('purchasers');
    const suppliers = useLookup('suppliers');
    const sellerCurrencyId = form.watch('currencyId') as string;
    const payId = form.watch('payCurrencyId') as string;
    const purchaserId = form.watch('purchaserId') as string;
    const supplierId = form.watch('supplierId') as string;
    const trough = String(form.watch('troughId') ?? '');
    const bankId = form.watch('bankId') as string;
    const amount = String(form.watch('payAmount') ?? '');
    const rate = String(form.watch('exchangeRate') ?? '');
    const code = (id: string) => currencies.data?.find((c) => String(c.id) === id)?.code as string | undefined;
    const sellerCode = code(sellerCurrencyId);
    const payCode = code(payId);
    // the same currency on both sides is simply a rate of 1: fixed and greyed out
    const sameCurrency = !!sellerCurrencyId && sellerCurrencyId === payId;

    const sellerRows = sellerCurrencyId ? (purchasers.data ?? []).filter((p) => holds(p, sellerCurrencyId)) : [];
    const dispatcherRows = payId ? (suppliers.data ?? []).filter((d) => holds(d, payId)) : [];
    const picker = useBankPicker(form, payId ? Number(payId) : undefined); // a Bank must be in the Pay currency
    const bank = picker.fields()[1] as FieldDef;
    const bankName = bank.options?.find((o) => o.value === bankId)?.label;
    const sellerName = (purchasers.data ?? []).find((p) => String(p.id) === purchaserId)?.name as string | undefined;
    const dispatcherName = (suppliers.data ?? []).find((d) => String(d.id) === supplierId)?.name as string | undefined;

    useEffect(() => {
      if (sameCurrency && form.getValues('exchangeRate') !== '1') form.setValue('exchangeRate', '1', { shouldValidate: form.formState.isSubmitted });
    }, [sameCurrency, rate, form]);
    useEffect(() => {
      if (purchaserId && sellerCurrencyId && !sellerRows.some((p) => String(p.id) === purchaserId)) form.setValue('purchaserId', '');
    }, [sellerCurrencyId, purchaserId, purchasers.data]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (supplierId && (trough !== DISPATCHER || (payId && !dispatcherRows.some((d) => String(d.id) === supplierId)))) form.setValue('supplierId', '');
    }, [payId, supplierId, trough, suppliers.data]); // eslint-disable-line react-hooks/exhaustive-deps

    // what the payment settles, and the Seller's balance afterwards (three decimals, like every Amount / Rate)
    const dues = lineTotal(amount, rate);
    const sellerBal = useBalance('purchasers', purchaserId, sellerCurrencyId);
    // what the chosen source holds (Cash, a Bank and a Dispatcher's account are separate balances): a payment bigger than it is refused by the API too
    const cashBal = useBalance('currencies', trough === TROUGH_CASH ? payId : undefined);
    const bankBal = useBalance('banks', trough === TROUGH_BANK ? bankId : undefined);
    const dispBal = useBalance('suppliers', trough === DISPATCHER ? supplierId : undefined, payId);
    const availableStr = trough === TROUGH_CASH ? cashBal.data?.balance : trough === TROUGH_BANK ? bankBal.data?.balance : dispBal.data?.balance;
    const short = mode === 'create' && availableStr !== undefined && !!amount && Number(amount.replace(/,/g, '')) > Number(availableStr);
    const balanceAfter = sellerBal.data && dues ? sumMoney([sellerBal.data.balance, dues], 3) : null;

    const field = (def: FieldDef) => <FieldRenderer form={form} field={{ ...def, col: 12 }} readOnly={readOnly} />;
    const currencyOptions = toOptions(currencies.data, (c) => c.code as string);
    const accent = (color: string, title: string, children: ReactNode) => (
      <div className="card h-100" style={{ borderLeft: `3px solid var(--bs-${color})` }}>
        <div className="card-body">
          <div className={`fw-semibold mb-2 text-${color}`}>{title}</div>
          {children}
        </div>
      </div>
    );

    const body = (
      <>
        <div className="d-flex justify-content-end mb-3">
          <div style={{ minWidth: 240 }}>{field({ name: 'transactionDate', label: 'Date and time', type: 'datetime', required: true })}</div>
        </div>
        <div className="row g-3 mb-3">
          <div className="col-lg-6">
            {accent('success', 'Pay to (seller)', (
              <>
                <div className="row">
                  <div className="col-sm-4">{field({ name: 'currencyId', label: 'Seller currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' })}</div>
                  <div className="col-sm-8">
                    {field({
                      name: 'purchaserId', label: `Seller${sellerCode ? ` (owed in ${sellerCode} only)` : ''}`, type: 'select', required: true, options: toOptions(sellerRows),
                      placeholder: sellerCurrencyId ? 'Select…' : 'Choose the seller currency first', disabled: !sellerCurrencyId,
                    })}
                  </div>
                </div>
                <BalanceCard kind="purchasers" id={purchaserId} currencyId={sellerCurrencyId} title="Seller balance (negative = you owe)" decimals={3} />
              </>
            ))}
          </div>
          <div className="col-lg-6">
            {accent('warning', 'Paid from (you pay)', (
              <>
                <Segmented form={form} name="troughId" label="Paid from" disabled={readOnly}
                  options={[
                    { value: TROUGH_CASH, label: 'Cash', variant: 'warning', icon: 'cash-coin' },
                    { value: TROUGH_BANK, label: 'Bank', variant: 'warning', icon: 'bank' },
                    { value: DISPATCHER, label: 'Dispatcher', variant: 'warning', icon: 'truck' },
                  ]} />
                <div className="row">
                  <div className="col-sm-4">{field({ name: 'payCurrencyId', label: 'Pay currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' })}</div>
                  <div className="col-sm-8">
                    {trough === DISPATCHER && field({
                      name: 'supplierId', label: `Dispatcher${payCode ? ` (${payCode} only)` : ''}`, type: 'select', required: true, options: toOptions(dispatcherRows),
                      placeholder: payId ? 'Select…' : 'Choose the pay currency first', disabled: !payId,
                    })}
                    {trough === TROUGH_BANK && field({ ...bank, label: `Bank${payCode ? ` (${payCode} only)` : ''}`, required: true, disabled: !payId, placeholder: payId ? 'Select a bank…' : 'Choose the pay currency first' })}
                    {trough === TROUGH_CASH && <div className="text-body-secondary small pt-4">Cash in hand{payCode ? ` (${payCode})` : ''}</div>}
                  </div>
                </div>
                {trough === TROUGH_CASH && <BalanceCard kind="currencies" id={payId} title={`Available in cash${payCode ? ` (${payCode})` : ''}`} />}
                {trough === TROUGH_BANK && <BalanceCard kind="banks" id={bankId} title="Available in this bank" />}
                {trough === DISPATCHER && <BalanceCard kind="suppliers" id={supplierId} currencyId={payId} title="Available in this dispatcher's account" />}
                {short && (
                  <Alert variant="danger" className="py-2 px-3 mt-2 mb-0 small" data-testid="not-enough">
                    <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden />
                    Not enough balance: the {trough === TROUGH_CASH ? 'cash in hand' : trough === TROUGH_BANK ? 'bank' : 'dispatcher'} holds <Amount value={availableStr} code={payCode} />, this payment needs <Amount value={amount} code={payCode} />.
                  </Alert>
                )}
              </>
            ))}
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="fw-semibold mb-2">The deal</div>
            <div className="row">
              <div className="col-md-4">{field({ name: 'payAmount', label: `You pay${payCode ? ` (${payCode})` : ''}`, type: 'money', required: true })}</div>
              <div className="col-md-4">
                {field({
                  name: 'exchangeRate', label: `Rate${sellerCode && payCode ? ` (1 ${sellerCode} = ? ${payCode})` : ''}`, type: 'money', required: true, disabled: sameCurrency,
                  help: sameCurrency ? 'Same currency: the rate is 1' : undefined,
                })}
              </div>
              <div className="col-md-4">
                <div className="form-label">Settles seller dues{sellerCode ? ` (${sellerCode})` : ''}</div>
                <div className="rounded border p-2 px-3 d-flex justify-content-between align-items-center text-success" style={{ background: 'rgba(25, 135, 84, 0.12)' }} data-testid="settles-dues">
                  <small>{sellerCode ?? ''}</small>
                  <strong className="fs-5"><Amount value={dues} decimals={3} /></strong>
                </div>
                {balanceAfter && sellerBal.data && (
                  <div className="small text-body-secondary mt-1" data-testid="balance-after">
                    Balance after: <Amount value={balanceAfter} decimals={3} code={sellerBal.data.currencyCode} />
                  </div>
                )}
              </div>
            </div>
            {field({ name: 'details', label: 'Remarks (optional)', type: 'textarea', placeholder: 'Add a note if needed' })}
          </div>
        </div>
      </>
    );

    // the books, as a journal line: the Seller is debited the dues settled, the source is credited what you pay
    const source = trough === DISPATCHER ? (dispatcherName ?? 'Dispatcher') : trough === TROUGH_BANK ? (bankName ?? 'Bank') : 'Cash';
    const summary = dues && payCode && sellerCode && amount ? (
      <span data-testid="payment-effect">
        Dr Seller {sellerName ?? ''} <Amount value={dues} decimals={3} code={sellerCode} /> / Cr {source} <Amount value={amount} code={payCode} />
      </span>
    ) : null;

    return { body, summary, fields: [] };
  },
};
